import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import React, { forwardRef, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { isModifiedClick } from "../../../../../utils/navigationClick";
import {
  AssignmentType,
  HomeAssignment,
  ProgressStatus,
} from "../../../../../services/api/types/general/homeAssignment";
import { hexToRgb } from "../../../../../utils/helpers";
import igaPlaceholder from "../../../../../assets/iga_gray.png";
import { useCustomisation } from "../../../../../context/CustomisationContext";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { ShadowSize } from "../../Dashboard/HomeBanner";
import { EllipsisTooltip } from "../../../../../components/EllipsisTooltip/EllipsisTooltip";

type HomeTranslate = ReturnType<typeof useTranslate<"home">>;

interface AssignmentCardColors {
  backgroundColor?: string;
  primaryText?: string;
  courses?: string;
  surveys?: string;
  policies?: string;
  overdue?: { text?: string; background?: string };
  date?: { text?: string; background?: string };
  progress?: {
    notStarted?: string;
    inProgress?: string;
    completed?: string;
  };
}

interface AssignmentCardProps {
  assignment: HomeAssignment;
  onClick: (assignment: HomeAssignment) => void;
  href?: string;
  colors: AssignmentCardColors;
  opacity?: { enabled: boolean; value: number };
  items?: React.ReactNode[];
  getProgressColor?: (progressStatus: string) => string | undefined;
}

const IMAGE_HEIGHT = 150;

const getAssignmentTypeColor = (
  type: AssignmentType,
  colors: AssignmentCardColors,
): string => {
  switch (type) {
    case AssignmentType.POLICY:
      return colors.policies || colors.primaryText || "inherit";
    case AssignmentType.SURVEY:
      return colors.surveys || colors.primaryText || "inherit";
    default:
      return colors.courses || colors.primaryText || "inherit";
  }
};

const getAssignmentTypeLabel = (
  type: AssignmentType,
  t: HomeTranslate,
): string => {
  switch (type) {
    case AssignmentType.POLICY:
      return t("filters", "policy");
    case AssignmentType.SURVEY:
      return t("filters", "survey");
    default:
      return t("filters", "course");
  }
};

const formatDueDate = (
  dateString: string,
  t: HomeTranslate,
): { label: string; isOverdue: boolean } => {
  const due = new Date(dateString);
  // Compare calendar days, not timestamps: a due date of today is stored as
  // midnight, so it must only become overdue once today has passed.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDueDay = new Date(due);
  startOfDueDay.setHours(0, 0, 0, 0);
  const isOverdue = startOfDueDay < startOfToday;
  const label = isOverdue
    ? t("assignments", "overdue")
    : due.toLocaleDateString("en-GB").replace(/\//g, ".");
  return { label, isOverdue };
};

const getProgressStatusLabel = (
  progressStatus: ProgressStatus,
  t: HomeTranslate,
): string => {
  switch (progressStatus) {
    case ProgressStatus.NOTSTARTED:
      return t("filters", "notStarted");
    case ProgressStatus.INPROGRESS:
      return t("filters", "inProgress");
    case ProgressStatus.COMPLETED:
      return t("banner", "completed");
    // TODO:"Functionality behind "Failed" option is not yet implemented, it will stay deprecated until IL-1610 is completed."
    // case ProgressStatus.FAILED:
    //   return t("filters", "failed");
    case ProgressStatus.NOTSTARTED_OVERDUE:
      return t("filters", "notStartedOverdue");
    case ProgressStatus.INPROGRESS_OVERDUE:
      return t("filters", "inProgressOverdue");
    case ProgressStatus.FAILED_OVERDUE:
      return t("filters", "completedOverdue");
    default:
      return progressStatus;
  }
};

export const AssignmentCard = forwardRef<HTMLDivElement, AssignmentCardProps>(
  (
    {
      assignment,
      onClick,
      href,
      colors,
      opacity,
      items = [],
      getProgressColor,
    },
    ref,
  ) => {
    const [imageError, setImageError] = useState(false);
    const imageSrc = imageError ? igaPlaceholder : `/${assignment.thumbnail}`;
    const { customisations } = useCustomisation();
    const t = useTranslate("home");
    const { HOME, GPC } = customisations.customisations;

    const secondaryTextColor =
      HOME.textColorsSettings?.homeSecondaryTextColor ??
      GPC.textColorsSettings?.gpcSecondaryTextColor;
    const shadowSize =
      HOME.cardsSettings?.cardShadowSize ??
      HOME.assignmentStatusColorsSettings?.homeShadowSize ??
      GPC.assignmentStatusColorsSettings?.gpcShadowSize;
    const shadowColor =
      HOME.cardsSettings?.cardShadowColor ??
      HOME.assignmentStatusColorsSettings?.homeShadowColor ??
      GPC.assignmentStatusColorsSettings?.gpcShadowColor;
    const cardBorderColor = HOME.cardsSettings?.cardBorderColor;
    const cardBorderSize = HOME.cardsSettings?.cardBorderSize;

    const shadowsMap: Record<ShadowSize, string | null> = {
      "N/A": null,
      XS: `0 1px 2px 0 ${shadowColor}`,
      SM: `0 1px 3px 0 ${shadowColor}`,
      MD: `0 4px 8px -2px ${shadowColor}`,
      LG: `0 12px 16px -4px ${shadowColor}`,
    };

    const dueDateInfo = useMemo(
      () => (assignment.dueDate ? formatDueDate(assignment.dueDate, t) : null),
      [assignment.dueDate, t],
    );

    const assignmentTypeColor = useMemo(
      () => getAssignmentTypeColor(assignment.assignmentType, colors),
      [assignment.assignmentType, colors],
    );

    const progressStatus = assignment.progressStatus;
    const isPreviewMode = assignment.mode === "preview";
    const isCompleted = progressStatus === ProgressStatus.COMPLETED;
    const isNotStarted = progressStatus === ProgressStatus.NOTSTARTED;
    const progressColor = getProgressColor?.(progressStatus);
    const isLocked = assignment.isLocked === true;
    const prerequisitesCount = assignment.prerequisites ?? 0;

    const displayProgress = isCompleted ? 100 : isNotStarted ? 0 : 50;

    const overlayOpacity = opacity?.enabled ? opacity.value : 0;

    const overlayColor = colors.backgroundColor
      ? `rgba(${hexToRgb(colors.backgroundColor)}, ${overlayOpacity})`
      : `rgba(255, 255, 255, ${overlayOpacity})`;

    return (
      <Card
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: colors.backgroundColor || "white",
          padding: 0,
          boxShadow: shadowsMap[shadowSize as ShadowSize],
          ...(cardBorderColor &&
            cardBorderSize && {
              border: `${cardBorderSize} solid ${cardBorderColor}`,
            }),
          width: "100%",
          height: "350px",
          // Smooth hover effect
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.02)",
          },
        }}
      >
        <CardActionArea
          {...(href ? { component: RouterLink, to: href } : {})}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            alignItems: "stretch",
          }}
          onClick={(e: React.MouseEvent) => {
            if (isModifiedClick(e)) return;
            e.preventDefault();
            onClick(assignment);
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "150px",
              flexShrink: 0,
            }}
          >
            <CardMedia
              component="img"
              height={IMAGE_HEIGHT}
              image={imageSrc}
              alt={assignment.titleShort}
              onError={() => setImageError(true)}
              sx={{
                width: "100%",
                height: "150px",
                objectFit: "cover",
                display: "block",
                ...(isLocked && { filter: "grayscale(100%) brightness(0.75)" }),
              }}
            />

            {opacity?.enabled && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  height: `${IMAGE_HEIGHT}px`,
                  bgcolor: overlayColor,
                  pointerEvents: "none",
                }}
              />
            )}

            {isLocked && (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "#F2F4F733",
                    backdropFilter: "blur(4px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 28, color: "#ffff" }} />
                </Box>
              </>
            )}

            {(isPreviewMode || (dueDateInfo && !isCompleted)) && (
              <Box
                sx={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "999px",
                  backgroundColor: isPreviewMode
                    ? "#FEF6EE"
                    : dueDateInfo!.isOverdue
                      ? colors.overdue?.background
                      : colors.date?.background,
                  border: `1px solid ${
                    isPreviewMode
                      ? "#F9DBAF"
                      : dueDateInfo!.isOverdue
                        ? colors.overdue?.text
                        : colors.date?.text
                  }`,
                }}
              >
                <Typography
                  sx={{
                    color: isPreviewMode
                      ? "#B93815"
                      : dueDateInfo!.isOverdue
                        ? colors.overdue?.text
                        : colors.date?.text,
                    fontWeight: 400,
                  }}
                >
                  {isPreviewMode
                    ? t("assignments", "preview")
                    : dueDateInfo?.label}
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              height: "200px",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                padding: "14px",
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack spacing={2} sx={{ flex: 1, overflow: "hidden" }}>
                {isLocked ? (
                  <Chip
                    icon={
                      <LockOutlinedIcon
                        sx={{
                          fontSize: "11px !important",
                          color: "#475467 !important",
                        }}
                      />
                    }
                    label={t("assignments", "locked")}
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #98A2B3",
                      color: "#344054",
                      fontSize: "12px",
                    }}
                  />
                ) : assignment.isDirectAssignment ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ color: assignmentTypeColor }}>
                      {getAssignmentTypeLabel(assignment.assignmentType, t)}
                    </Typography>
                    {parseInt(assignment.duration) > 0 && (
                      <Typography sx={{ color: secondaryTextColor }}>
                        {t("assignments", "durationMinutes", {
                          duration: assignment.duration,
                        })}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    spacing={0.5}
                  >
                    {items.map((item, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <Typography sx={{ color: "text.secondary" }}>
                            •
                          </Typography>
                        )}
                        <Box sx={{ whiteSpace: "nowrap" }}>{item}</Box>
                      </React.Fragment>
                    ))}
                  </Stack>
                )}

                <Typography variant="h4" color={secondaryTextColor}>
                  <EllipsisTooltip
                    title={assignment.titleShort || assignment.title}
                    lines={2}
                  >
                    {assignment.titleShort || assignment.title}
                  </EllipsisTooltip>
                </Typography>

                {isLocked && prerequisitesCount > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    {prerequisitesCount}{" "}
                    {prerequisitesCount === 1
                      ? t("assignments", "assignmentRequired")
                      : t("assignments", "assignmentsRequired")}
                  </Typography>
                )}
              </Stack>
            </CardContent>

            {!isLocked && assignment.isDirectAssignment && progressStatus && (
              <Box padding="1rem" sx={{ flexShrink: 0 }}>
                <Typography mb={0.5} color={progressColor}>
                  {getProgressStatusLabel(progressStatus, t)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: progressColor,
                    },
                    backgroundColor: "lightgray",
                  }}
                  value={displayProgress}
                />
              </Box>
            )}
          </Box>
        </CardActionArea>
      </Card>
    );
  },
);
